
import { agentDisplayLabel } from '@client/features/users';
import { classifyBookingOwner, type BookingOwner } from '@client/shared/lib/booking-owner.ts';

export interface BookingDisplaySource {
  readonly id?: string | null;
  readonly startsAt?: string | null;
  readonly endsAt?: string | null;
  readonly agentUserId?: string | null;
}

export interface ListingAddressSource {
  readonly formattedAddress?: string | null;
  readonly addressLine1?: string | null;
  readonly city?: string | null;
}

export interface BookingDisplayContext {
  readonly viewerUserId: string | null;
  readonly agentNames: ReadonlyMap<string, string> | undefined;
}

export interface BookingDisplay {
  readonly id: string;
  readonly start: string;
  readonly end: string;
  readonly address: string;
  readonly agentLabel: string | null;
  readonly owner: BookingOwner;
}

export interface BookingDisplayOptions {
  readonly fallbackAddress: string;
}

export function listingDisplayAddress(
  listing: ListingAddressSource | null | undefined,
  fallback: string,
): string {
  if (!listing) return fallback;
  return (
    listing.formattedAddress ||
    [listing.addressLine1, listing.city].filter(Boolean).join(', ') ||
    fallback
  );
}

export function toBookingDisplay(
  booking: BookingDisplaySource,
  listing: ListingAddressSource | null | undefined,
  context: BookingDisplayContext,
  options: BookingDisplayOptions,
): BookingDisplay | null {
  if (!booking.id || !booking.startsAt || !booking.endsAt) return null;
  const agentUserId = booking.agentUserId ?? null;
  return {
    id: booking.id,
    start: booking.startsAt,
    end: booking.endsAt,
    address: listingDisplayAddress(listing, options.fallbackAddress),
    agentLabel: agentDisplayLabel(agentUserId, context.viewerUserId, context.agentNames),
    owner: classifyBookingOwner(agentUserId, context.viewerUserId),
  };
}
